import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Compass, MessageCircle } from 'lucide-react';
import { SiAndroid, SiApple } from 'react-icons/si';
import { getPostLoginTour, useTourGuide } from '@/components/TourGuide';
import { getClientLinks, publicAssetUrl } from '@/config/clientLinksEnv';
import { useSurface } from '@/contexts/SurfaceContext';
import { isIosDevice } from '@/hooks/useInstallPrompt';
import { useInstallLink, useIosGuide, type IosGuideState, type SupportLink } from './installAppLink';

export type { IosGuideState, SupportLink, SupportLinkId } from './installAppLink';

/**
 * The one list of "more ways to get help" and app links (spec 2026-10-08-kiosk-web-split §5):
 * the side strip (SideStrip) and Help's "More ways to get help" read their items from here, and
 * Install app (also Me's row) from installAppLink.tsx, so a link, its label and what it does are
 * decided once.
 *
 * Callers play the click sound where they render the item (IconButton does it itself).
 */

/** Where the links show: the side strip, or Help's "More ways to get help". */
export type SupportLinksPlacement = 'strip' | 'help';

function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * The links for a placement, in order. Kiosks and an unknown surface get no external links and no
 * app downloads (src/config/surfacePolicy.ts).
 *
 * - `strip`: Install app, Ask us, Telegram, Android app (not on iOS), Take the tour. On iOS,
 *   Install app opens the iPhone guide, so the strip has no separate iPhone guide item.
 * - `help` (Help's "More ways", unchanged rows plus Install app): Telegram, Android app, Install
 *   app (browser prompt only: on iOS the iPhone app guide row is that), iPhone app guide, Take the
 *   tour. Ask us is Help's own main button, so it is not in this list.
 */
export function useSupportLinks(placement: SupportLinksPlacement): { links: SupportLink[]; iosGuide: IosGuideState } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useSurface();
  const { openWelcome, start, close } = useTourGuide();
  const guide = useIosGuide();
  const install = useInstallLink(guide.show);
  const { telegramUrl, androidApkUrl } = getClientLinks();
  const showExternalLinks = can('externalLinks');
  const showAppDownloads = can('appDownloads');
  const onIos = isIosDevice();

  const askUs: SupportLink = {
    id: 'askUs',
    label: t('help.ask'),
    hint: t('sideStrip.askHint'),
    icon: <MessageCircle />,
    external: false,
    onSelect: () => navigate('/help?tab=new'),
  };
  const telegram: SupportLink | null =
    showExternalLinks && telegramUrl
      ? {
          id: 'telegram',
          label: t('help.more.telegram'),
          hint: t('help.more.telegramHint'),
          icon: <img src={publicAssetUrl('telegram-mark.png')} alt="" className="block size-6 object-contain" draggable={false} />,
          external: true,
          onSelect: () => openExternal(telegramUrl),
        }
      : null;
  const android: SupportLink | null =
    showAppDownloads && androidApkUrl && (placement === 'help' || !onIos)
      ? {
          id: 'android',
          label: t('help.more.android'),
          hint: t('help.more.androidHint'),
          icon: <SiAndroid />,
          external: true,
          onSelect: () => openExternal(androidApkUrl),
        }
      : null;
  const iphoneGuide: SupportLink | null =
    placement === 'help' && showAppDownloads
      ? {
          id: 'iphoneGuide',
          label: t('help.more.iphone'),
          hint: t('help.more.iphoneHint'),
          icon: <SiApple />,
          external: false,
          onSelect: guide.show,
        }
      : null;
  // "Show me around": the app tour (tabs and header), not the current page's own tour.
  const tour: SupportLink = {
    id: 'tour',
    label: t('help.more.tour'),
    hint: t('help.more.tourHint'),
    icon: <Compass />,
    external: false,
    onSelect: () => {
      close();
      openWelcome(getPostLoginTour().steps);
      start();
    },
  };

  const links =
    placement === 'strip'
      ? [install, askUs, telegram, android, tour]
      : [telegram, android, install && !onIos ? install : null, iphoneGuide, tour];
  return { links: links.filter((link): link is SupportLink => link !== null), iosGuide: guide.state };
}

/** Leading tile for a support link row (Help's "More ways", the strip's list on phones). */
export function SupportLinkTile({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-control border border-edge-subtle bg-inset text-primary [&>svg]:size-5"
    >
      {children}
    </span>
  );
}
